import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { Document, Page } from 'react-pdf';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import Modal from 'components/Modal';
import Button from 'components/UI/Button';
import useLiterals from 'utils/hooks/useLiterals';
import { ReactComponent as CloseIcon } from 'assets/icons/close.svg';
import configurePdfWorker from '../utils/configurePdfWorker';

const ZOOM_SCALES = [1, 1.25, 1.5, 2];

const LegalDocumentViewer = ({ document: legalDocument, onClose }) => {
  configurePdfWorker();

  const titleId = useId();
  const closeButtonRef = useRef(null);
  const pagesRef = useRef(null);
  const literals = useLiterals('digitalLoan');
  const [pageWidth, setPageWidth] = useState(0);
  const [numPages, setNumPages] = useState(null);
  const [scaleIndex, setScaleIndex] = useState(0);
  const [hasError, setHasError] = useState(false);
  const [retryKey, setRetryKey] = useState(0);
  const [activeDocumentId, setActiveDocumentId] = useState(legalDocument.id);

  if (legalDocument.id !== activeDocumentId) {
    setActiveDocumentId(legalDocument.id);
    setNumPages(null);
    setScaleIndex(0);
    setHasError(false);
    setRetryKey(0);
  }

  const scale = ZOOM_SCALES[scaleIndex];

  useEffect(() => {
    closeButtonRef.current?.focus();
  }, []);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    window.document.addEventListener('keydown', handleKeyDown);
    return () => window.document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  useEffect(() => {
    const node = pagesRef.current;
    if (!node) return undefined;

    const applyWidth = (value) => {
      const nextWidth = Math.floor(value);
      if (nextWidth > 0) {
        setPageWidth((current) => (current === nextWidth ? current : nextWidth));
      }
    };

    applyWidth(node.clientWidth);

    if (typeof ResizeObserver === 'undefined') return undefined;

    const observer = new ResizeObserver((entries) => {
      const observed = entries[0]?.contentRect?.width;
      applyWidth(observed || node.clientWidth);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const handleLoadSuccess = useCallback(({ numPages: nextNumPages }) => {
    setNumPages(nextNumPages);
    setHasError(false);
  }, []);

  const handleLoadError = useCallback(() => {
    setHasError(true);
    setNumPages(null);
  }, []);

  const handleRetry = () => {
    setHasError(false);
    setNumPages(null);
    setRetryKey((current) => current + 1);
  };

  const handleZoomIn = () => {
    setScaleIndex((current) => Math.min(current + 1, ZOOM_SCALES.length - 1));
  };

  const handleZoomOut = () => {
    setScaleIndex((current) => Math.max(current - 1, 0));
  };

  const pages = numPages
    ? Array.from({ length: numPages }, (_, index) => index + 1)
    : [];

  return (
    <Modal
      fullScreen
      showCloseButton={false}
      closeOnBackgroundClick={false}
      noContentPadding
      animation=''
      onClose={onClose}
      className='digital-loan-legal-viewer-modal'
    >
      <div
        className='digital-loan-legal-viewer'
        role='dialog'
        aria-modal='true'
        aria-labelledby={titleId}
      >
        <div className='generic-modal__header-content digital-loan-legal-viewer__header'>
          <h2 id={titleId} className='generic-modal__title'>
            {legalDocument.title}
          </h2>
          <button
            ref={closeButtonRef}
            type='button'
            className='generic-modal__close-button'
            aria-label={literals.common.close}
            onClick={onClose}
          >
            <CloseIcon />
          </button>
        </div>
        {numPages && !hasError && (
          <div className='digital-loan-legal-viewer__toolbar'>
            <button
              type='button'
              className='digital-loan-legal-viewer__zoom'
              aria-label={literals.permissions.viewer.zoomOut}
              onClick={handleZoomOut}
              disabled={scaleIndex === 0}
            >
              −
            </button>
            <button
              type='button'
              className='digital-loan-legal-viewer__zoom'
              aria-label={literals.permissions.viewer.zoomIn}
              onClick={handleZoomIn}
              disabled={scaleIndex === ZOOM_SCALES.length - 1}
            >
              +
            </button>
          </div>
        )}
        <div
          ref={pagesRef}
          className={
            `digital-loan-legal-viewer__pages${
              scale > 1 ? ' digital-loan-legal-viewer__pages--zoomed' : ''
            }`
          }
        >
          {hasError ? (
            <div className='digital-loan-legal-viewer__error' role='alert'>
              <p>{literals.permissions.viewer.loadError}</p>
              <div className='digital-loan-legal-viewer__error-actions'>
                <Button text={literals.common.retry} onClick={handleRetry} />
                <Button
                  text={literals.common.close}
                  type='secondary'
                  onClick={onClose}
                />
              </div>
            </div>
          ) : (
            <Document
              key={`${legalDocument.id}-${retryKey}`}
              file={legalDocument.file}
              onLoadSuccess={handleLoadSuccess}
              onLoadError={handleLoadError}
              loading={(
                <p
                  className='digital-loan-legal-viewer__status'
                  role='status'
                  aria-live='polite'
                >
                  {literals.common.loading}
                </p>
              )}
            >
              {pageWidth > 0 && pages.map((pageNumber) => (
                <Page
                  key={`${legalDocument.id}-page-${pageNumber}`}
                  className='digital-loan-legal-viewer__page'
                  pageNumber={pageNumber}
                  width={pageWidth}
                  scale={scale}
                  renderAnnotationLayer={false}
                  renderTextLayer
                  loading={null}
                />
              ))}
            </Document>
          )}
        </div>
      </div>
    </Modal>
  );
};

LegalDocumentViewer.propTypes = {
  document: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    file: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        data: PropTypes.instanceOf(Uint8Array),
      }),
    ]).isRequired,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
};

export default LegalDocumentViewer;
